import * as React from 'react';
import { IntlShape } from 'react-intl';
import { Tag } from 'app/components/tag';
import { ButtonCallToAction } from 'ui';
import { Expandable, ShouldTruncate, standardTextLimit } from 'ui/expandable';
import { replaceTemplateString } from 'app/infrastructure/platform/text';
import { formatDate, getDraftExpiresInDays } from '../utils';
import { CaseDraftsMeatball } from './case-drafts-tiles-meatball';
import { MyCaseDraftsTranslation } from 'app/area/my-case-drafts/constants';
import { CaseDraftRow } from 'app/area/my-case-drafts/types-store';
import * as navigate from 'app/area/shared/navigation';
import styles from './case-drafts-tile.scss';

interface CaseDraftsTileProps {
  row: CaseDraftRow;
  onDelete: (draftId: number) => void;
  translation: MyCaseDraftsTranslation;
  intl: IntlShape;
  portalTarget?: HTMLElement | null;
}

export const CaseDraftsTile = React.memo(({ row, onDelete, translation, intl, portalTarget }: CaseDraftsTileProps) => (
  <div className={styles.caseDraftTileWrapper}>
  <div className={styles.caseDraftTile}>
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <Tag className={styles.tag}>{translation.my_case_drafts_tag}</Tag>
        <span className={styles.caseType}>{row.caseType?.ld ?? ''}</span>
      </div>
      <div className={styles.headerRight}>
        <ButtonCallToAction
          classNames={styles.continueButton}
          onClick={() => navigate.toDraftCaseViewModal(row.draftId)}>
          {translation.my_case_drafts_continue}
        </ButtonCallToAction>
        <CaseDraftsMeatball draftId={row.draftId} onDelete={onDelete} translation={translation} portalTarget={portalTarget} />
      </div>
    </div>
    <h1 className={styles.title}>
      <button type="button" className={styles.titleLink} onClick={() => navigate.toDraftCaseViewModal(row.draftId)}>
        {row.caseTitle || translation.my_case_drafts_untitled_draft}
      </button>
    </h1>
    <div className={styles.publishWarning}>
      <p>{translation.my_case_drafts_warning}</p>
    </div>
    <div className={styles.metaRow}>
      <span className={styles.metaItem}>{translation.my_case_drafts_case_date} {formatDate(row.caseDate, intl)}</span>
      <span className={styles.metaItem}>{translation.my_case_drafts_last_modified_date} {formatDate(row.modifiedDate, intl)}</span>
      {!!getDraftExpiresInDays(row.lockedDate) && (
        <span className={styles.metaItem}>
          {replaceTemplateString(
            getDraftExpiresInDays(row.lockedDate) === 1
              ? translation.my_case_drafts_expires_in_day
              : translation.my_case_drafts_expires_in_days,
            getDraftExpiresInDays(row.lockedDate)
          )}
        </span>
      )}
    </div>
    {row.caseDescription && (
      <p className={styles.description}>
        {ShouldTruncate(row.caseDescription) ? (
          <Expandable
            content={(collapsed) => (collapsed ? `${row.caseDescription.substring(0, standardTextLimit)}...` : row.caseDescription)}
            textDisplayMore={translation.my_case_drafts_show_more}
            textDisplayLess={translation.my_case_drafts_show_less} />
        ) : row.caseDescription}
      </p>
    )}
    <ButtonCallToAction
      classNames={styles.continueButtonMobile}
      onClick={() => navigate.toDraftCaseViewModal(row.draftId)}>
      {translation.my_case_drafts_continue}
    </ButtonCallToAction>
  </div>
  </div>
));
